// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import type { NotificationRecord, NotificationCategory } from '@vn-grc/shared';
import { NotificationItem } from './NotificationItem.js';
import { apiClient } from '../services/api-client.js';

interface NotificationDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  t: Record<string, any>;
}

type FilterTab = 'all' | 'unread' | 'tasks' | 'approvals' | 'alerts';

const TASK_CATEGORIES: NotificationCategory[] = [
  'task_assigned',
  'task_sla_approaching',
  'task_overdue',
  'task_completed',
  'task_escalated',
];

const APPROVAL_CATEGORIES: NotificationCategory[] = ['approval_requested'];
const ALERT_CATEGORIES: NotificationCategory[] = ['sla_breach_report', 'system_alert'];

/**
 * Slide-out notification drawer.
 * Sprint 17.3.0: Notification System (Phase 15)
 */
export function NotificationDrawer({ isOpen, onClose, t }: NotificationDrawerProps) {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<NotificationRecord[]>([]);
  const [activeTab, setActiveTab] = useState<FilterTab>('all');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);
  const nt = t.notifications?.drawer ?? {};

  const fetchNotifications = useCallback(
    async (pageNum: number, reset = false) => {
      setLoading(true);
      try {
        const params: Record<string, string> = {
          page: String(pageNum),
          page_size: '20',
        };

        if (activeTab === 'unread') params.status = 'unread';
        if (activeTab === 'tasks') params.category = TASK_CATEGORIES.join(',');
        if (activeTab === 'approvals') params.category = APPROVAL_CATEGORIES.join(',');
        if (activeTab === 'alerts') params.category = ALERT_CATEGORIES.join(',');

        const result = (await apiClient.listNotifications(params)) as {
          data?: NotificationRecord[];
        };
        const items = result.data ?? [];

        if (reset) {
          setNotifications(items);
        } else {
          setNotifications((prev) => [...prev, ...items]);
        }
        setHasMore(items.length === 20);
      } catch {
        // Silently fail — notifications are non-critical
      } finally {
        setLoading(false);
      }
    },
    [activeTab],
  );

  useEffect(() => {
    if (isOpen) {
      setPage(1);
      fetchNotifications(1, true);
    }
  }, [isOpen, activeTab, fetchNotifications]);

  const handleTabChange = (tab: FilterTab) => {
    setActiveTab(tab);
    setPage(1);
    setHasMore(true);
  };

  const handleLoadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchNotifications(next);
  };

  const handleNotificationClick = async (notification: NotificationRecord) => {
    if (notification.status === 'unread') {
      await apiClient.markNotificationRead(notification._id).catch(() => {});
      setNotifications((prev) =>
        prev.map((n) => (n._id === notification._id ? { ...n, status: 'read' as const } : n)),
      );
    }
    if (notification.link_url) {
      navigate(notification.link_url);
    }
    onClose();
  };

  const handleMarkAllRead = async () => {
    await apiClient.markAllNotificationsRead().catch(() => {});
    setNotifications((prev) => prev.map((n) => ({ ...n, status: 'read' as const })));
  };

  // Close on Escape
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleKey);
      drawerRef.current?.focus();
    }
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Group by date
  const today = new Date().toDateString();
  const yesterday = new Date(Date.now() - 86400000).toDateString();

  const groups: { label: string; items: NotificationRecord[] }[] = [];
  const todayItems: NotificationRecord[] = [];
  const yesterdayItems: NotificationRecord[] = [];
  const olderItems: NotificationRecord[] = [];

  for (const n of notifications) {
    const d = new Date(n.created_at).toDateString();
    if (d === today) todayItems.push(n);
    else if (d === yesterday) yesterdayItems.push(n);
    else olderItems.push(n);
  }

  if (todayItems.length) groups.push({ label: 'Today', items: todayItems });
  if (yesterdayItems.length) groups.push({ label: 'Yesterday', items: yesterdayItems });
  if (olderItems.length) groups.push({ label: 'Older', items: olderItems });

  const tabs: { key: FilterTab; label: string }[] = [
    { key: 'all', label: nt.filter?.all ?? 'All' },
    { key: 'unread', label: nt.filter?.unread ?? 'Unread' },
    { key: 'tasks', label: nt.filter?.tasks ?? 'Tasks' },
    { key: 'approvals', label: nt.filter?.approvals ?? 'Approvals' },
    { key: 'alerts', label: nt.filter?.alerts ?? 'Alerts' },
  ];

  return (
    <>
      <div className="notif-overlay" onClick={onClose} aria-hidden="true" />
      <div
        className="notif-drawer"
        ref={drawerRef}
        role="dialog"
        aria-label={nt.title ?? 'Notifications'}
        tabIndex={-1}
      >
        <div className="notif-drawer-header">
          <h2>{nt.title ?? 'Notifications'}</h2>
          <div className="notif-drawer-actions">
            <button className="btn-text" onClick={handleMarkAllRead} type="button">
              {nt.mark_all_read ?? 'Mark all as read'}
            </button>
            <button className="btn-close" onClick={onClose} aria-label="Close" type="button">
              &times;
            </button>
          </div>
        </div>

        <div className="notif-drawer-tabs" role="tablist">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              className={`notif-tab ${activeTab === tab.key ? 'notif-tab-active' : ''}`}
              onClick={() => handleTabChange(tab.key)}
              role="tab"
              aria-selected={activeTab === tab.key}
              type="button"
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="notif-drawer-body">
          {groups.length === 0 && !loading && (
            <div className="notif-empty">
              {nt.empty ?? "No notifications. You're all caught up!"}
            </div>
          )}

          {groups.map((group) => (
            <div key={group.label} className="notif-group">
              <div className="notif-group-label">{group.label}</div>
              {group.items.map((n) => (
                <NotificationItem
                  key={n._id}
                  notification={n}
                  onClick={handleNotificationClick}
                  t={t}
                />
              ))}
            </div>
          ))}

          {hasMore && !loading && (
            <button className="notif-load-more" onClick={handleLoadMore} type="button">
              Load more
            </button>
          )}
          {loading && <div className="notif-loading">Loading...</div>}
        </div>
      </div>
    </>
  );
}
